import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { PrelineLoader } from '@core/components/PrelineLoader';
import { UpdateAvailableBanner } from '@core/components/UpdateAvailableBanner';
import { Spinner } from '@core/components/ui';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { selectIsGuest } from '@features/auth';
import { AccountMenu } from '@features/workspace/components/AccountMenu';
import { ImpersonationBanner } from '@features/workspace/components/ImpersonationBanner';
import { WorkspaceSidebar } from '@features/workspace/components/WorkspaceSidebar';
import { ProjectSelector } from '@features/workspace/components/ProjectSelector';
import { ChatPanel } from '@features/workspace/components/ChatPanel';
import { CanvasPanel } from '@features/workspace/components/CanvasPanel';
import { QuickPreview } from '@features/workspace/components/QuickPreview';
import { WorkspaceTour } from '@features/workspace/components/WorkspaceTour';
import { selectSidebarCollapsed, toggleSidebar } from '@features/workspace';
import {
  useGetConversationsQuery,
  selectCurrentConversationUuid,
  setCurrentConversation,
  startNewConversation,
} from '@features/ai';
import { FirstLabelGate, useCreateFirstLabelRunMutation, useFirstLabelRun } from '@features/dataroom';
import { useWorkspaceProject } from '@features/projects';

function SidebarToggleIcon({ collapsed }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3.5" y="4" width="17" height="16" rx="2.5" />
      <path d="M9 4v16" />
      <path d={collapsed ? 'm13 9 3 3-3 3' : 'm16 9-3 3 3 3'} />
    </svg>
  );
}

SidebarToggleIcon.propTypes = {
  collapsed: PropTypes.bool.isRequired,
};

export function WorkspaceLayout() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { shortId } = useParams();
  const currentConversationUuid = useAppSelector(selectCurrentConversationUuid);
  const { currentProject, isResolving, isError: projectsError, isFetching: projectsFetching, refetch: retryProjects } = useWorkspaceProject();
  const sidebarCollapsed = useAppSelector(selectSidebarCollapsed);
  const isGuest = useAppSelector(selectIsGuest);
  const projectUuid = currentProject?.uuid || undefined;
  // `currentData` so the shortId below is matched against THIS project's
  // conversations; `data` would still hold the previous project's list while
  // the new one is in flight, and a wrong/failed match never opens the chat.
  const { currentData: conversations = [] } = useGetConversationsQuery(projectUuid, { skip: !projectUuid });

  // A project is not usable until its First Label Analysis and public
  // dataroom exist: while the run is active (or failed) the whole content
  // area is replaced by the progress gate.
  const { run: firstLabelRun, isBlocking, hasDeliverables } = useFirstLabelRun(projectUuid);
  const [createFirstLabelRun, { isLoading: isRetrying }] = useCreateFirstLabelRunMutation();

  // Sync URL shortId → Redux conversation state
  useEffect(() => {
    if (shortId) {
      const match = conversations.find((c) => c.short_id === shortId);
      if (match && match.uuid !== currentConversationUuid) {
        dispatch(setCurrentConversation(match.uuid));
      }
    } else if (currentConversationUuid) {
      dispatch(startNewConversation());
    }
  }, [shortId, conversations]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex h-screen flex-col bg-white">
      <PrelineLoader />
      <WorkspaceTour active={Boolean(currentProject && firstLabelRun && hasDeliverables && !isBlocking)} />
      {/* shrink-0 bar above the header: renders null unless impersonating,
          so the member layout is untouched. */}
      <ImpersonationBanner />
      <UpdateAvailableBanner />
      <header className="grid h-15 shrink-0 grid-cols-[1fr_auto_1fr] items-center border-b border-[var(--color-border)] bg-white px-3 shadow-[0_1px_0_rgba(6,37,158,0.03)] sm:px-4">
        <div className="flex justify-start">
          {currentProject && (
            <button
              type="button"
              onClick={() => dispatch(toggleSidebar())}
              title={sidebarCollapsed ? t('core.workspaceLayout.expandSidebar') : t('core.workspaceLayout.collapseSidebar')}
              aria-label={sidebarCollapsed ? t('core.workspaceLayout.expandSidebar') : t('core.workspaceLayout.collapseSidebar')}
              className="flex size-9 cursor-pointer items-center justify-center rounded-lg border border-[var(--color-border-strong)] bg-white text-[var(--color-accent)] shadow-sm transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-subtle)]"
            >
              <SidebarToggleIcon collapsed={sidebarCollapsed} />
            </button>
          )}
        </div>
        <div className="flex min-w-0 items-center justify-center">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[var(--color-accent-subtle)] text-sm font-bold text-[var(--color-accent)] ring-1 ring-inset ring-[var(--color-accent)]/10">S</div>
          {/* Below sm the square mark stands alone: the header cannot fit the
              wordmark, the project selector and the account menu side by side. */}
          <div className="ml-2.5 hidden min-w-0 sm:block">
            <p data-testid="app-brand" className="truncate text-sm font-bold tracking-[-0.01em] text-[var(--color-text)]">Neovision Platform</p>
            <p className="hidden text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-text-muted)] sm:block">{t('core.workspaceLayout.tagline')}</p>
          </div>
        </div>
        {/* Always mounted: the account menu is the only way to switch
            organization, and the sidebar that used to host it is absent
            without a project and on the collapsed rail. */}
        <div className="flex min-w-0 items-center justify-end gap-1.5 sm:gap-2">
          {currentProject && <ProjectSelector variant="header" />}
          <AccountMenu />
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        {currentProject && <WorkspaceSidebar />}
        {!currentProject ? (
        <main className="flex min-w-0 flex-1 items-center justify-center bg-[var(--color-bg)] p-6">
          {projectsError && !projectsFetching ? (
            <div role="alert" className="text-center">
              <p className="text-sm text-[var(--color-text-secondary)]">{t('core.workspaceLayout.projectsError')}</p>
              <button type="button" onClick={retryProjects} className="mt-4 cursor-pointer rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-white">{t('core.workspaceLayout.tryAgain')}</button>
            </div>
          ) : isResolving ? (
            <div role="status" className="flex flex-col items-center gap-3 text-sm text-[var(--color-text-muted)]">
              <Spinner size="lg" />
              <p>{t('core.workspaceLayout.loadingProjects')}</p>
            </div>
          ) : isGuest ? (
              <div className="w-full max-w-xl rounded-2xl border border-[var(--color-border)] bg-white px-8 py-10 text-center shadow-sm sm:px-12 sm:py-12" data-testid="guest-empty-workspace">
                <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-[var(--color-accent-subtle)] text-[var(--color-accent)]">
                  <Icon icon="mdi:folder-account-outline" className="size-7" />
                </div>
                <h1 className="mt-6 text-2xl font-semibold text-[var(--color-text)]">{t('core.workspaceLayout.guestEmptyTitle')}</h1>
                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[var(--color-text-secondary)]">
                  {t('core.workspaceLayout.guestEmptyBody')}
                </p>
              </div>
          ) : (
          <div className="w-full max-w-xl rounded-2xl border border-[var(--color-border)] bg-white px-8 py-10 text-center shadow-sm sm:px-12 sm:py-12">
            <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-[var(--color-accent-subtle)] text-[var(--color-accent)]">
              <Icon icon="mdi:briefcase-search-outline" className="size-7" />
            </div>
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-accent)]">{t('core.workspaceLayout.firstRunEyebrow')}</p>
            <h1 className="mt-2 text-2xl font-semibold text-[var(--color-text)]">{t('core.workspaceLayout.firstRunTitle')}</h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[var(--color-text-secondary)]">
              {t('core.workspaceLayout.firstRunBody')}
            </p>
            <div className="mx-auto mt-7 w-full max-w-xs">
              <ProjectSelector variant="first-run" />
            </div>
            <p className="mt-5 text-xs leading-5 text-[var(--color-text-muted)]">
              {t('core.workspaceLayout.firstRunFooter')}
            </p>
          </div>
          )}
        </main>
        ) : isBlocking ? (
        <FirstLabelGate
          run={firstLabelRun}
          companyName={currentProject?.name}
          onRetry={isGuest ? undefined : () => createFirstLabelRun({ projectUuid, company: currentProject?.name }).catch(() => {})}
          isRetrying={isRetrying}
        />
        ) : (
        <>
          <ChatPanel />
          <CanvasPanel />
          <QuickPreview />
        </>
        )}
      </div>
    </div>
  );
}
